import React, { useEffect, useState } from 'react';
import { Col, Row } from 'antd';
import Step2 from './Step2';
import OfferDisplay from './OfferDisplay';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { getResellerOnboardingState } from '@/store/resellerOnboardingSteps/selector';
import { setOnboardingInfo } from '@/store/resellerOnboardingSteps/slice';
import LocalStorageUtility from '@/utils/localStorage';
import { LocalStorageKeys } from 'src/enums/ELocalStorage';
import posthog from 'posthog-js';
import { useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { verifyCustomOffer } from '@/api/customOffer';
import { Loading } from '@/components/Loading';
import Calendly from './Calendly';
import Unqualified from './Unqualified';
import logo from '@/assets/images/new_logo.png';
import { CheckCircleFilled, TrophyFilled } from '@ant-design/icons';

const ResellerQualificationForm: React.FC = () => {
	const location = useLocation();
	const { encodedData } = useParams<{ encodedData: string }>();
	const customOffer = location.pathname.includes('custom-offer-reseller');
	const { currentStep } = useAppSelector(getResellerOnboardingState);
	const dispatch = useAppDispatch();
	const [offerViewed, setOfferViewed] = useState(false);
	const [isStarting, setIsStarting] = useState(false);

	// Extract customer name from URL parameters
	const urlParams = new URLSearchParams(location.search);
	const customerName = urlParams.get('name');

	useEffect(() => {
		// Load saved state from localStorage when component mounts
		posthog.capture('Reseller_Registration_Landing_Page');
		const savedState = LocalStorageUtility.getLocalData(LocalStorageKeys.RESELLER_ONBOARDING);
		if (savedState && !customOffer) {
			dispatch(setOnboardingInfo(savedState));
		}
	}, []);

	const {
		data: verifyCustomOfferData,
		isLoading: verifyCustomOfferLoading,
		error: verifyCustomOfferError,
	} = useQuery({
		queryKey: ['custom-offer-reseller', encodedData],
		queryFn: () => verifyCustomOffer(encodedData),
		enabled: !!encodedData,
		retry: false,
	});

	const handleStartClick = () => {
		setIsStarting(true);
		// Simulate a small delay for better UX
		setTimeout(() => {
			setOfferViewed(true);
			setIsStarting(false);
		}, 500);
	};

	if (verifyCustomOfferLoading) {
		return <Loading />;
	}

	// Show error if custom offer is invalid
	if (
		customOffer &&
		!verifyCustomOfferLoading &&
		((verifyCustomOfferData && !verifyCustomOfferData.success) || verifyCustomOfferError)
	) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
				<div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
					<div className="mb-6">
						<svg
							className="mx-auto h-16 w-16 text-red-500"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
							/>
						</svg>
					</div>
					<h2 className="text-2xl font-bold text-gray-900 mb-4">Invalid Offer</h2>
					<p className="text-gray-600 mb-6">
						Sorry, this custom offer is no longer valid or doesn't exist. Please contact our support
						team for assistance.
					</p>
					<button
						onClick={() => window.history.back()}
						className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
					>
						Go Back
					</button>
				</div>
			</div>
		);
	}

	// Show offer display if custom offer is valid and not yet viewed
	if (customOffer && !verifyCustomOfferLoading && verifyCustomOfferData?.success && !offerViewed) {
		return (
			<OfferDisplay
				offerData={verifyCustomOfferData.data}
				onStartClick={handleStartClick}
				isLoading={isStarting}
				customerName={customerName}
			/>
		);
	}

	const renderStep = () => {
		switch (currentStep) {
			case 1:
				return <Step2 customOffer={customOffer} encodedData={encodedData} />;
			case 2:
				return <Unqualified />;
			case 3:
				return <Calendly />;
			default:
				return <Step2 customOffer={customOffer} encodedData={encodedData} />;
		}
	};

	// const steps = [
	// 	{ number: 1, label: 'Setup' },
	// 	{ number: 2, label: 'Selection' },
	// ];

	// const handleStepChange = (direction: 'prev' | 'next') => {
	// 	const newStep = direction === 'prev' ? currentStep - 1 : currentStep + 1;
	// 	if (newStep >= 1 && newStep <= 2) {
	// 		dispatch(setStep(newStep));
	// 	}
	// };

	return (
		<>
			<Row className="h-screen overflow-md-hidden">
				<div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20">
					<div className="bg-white rounded-xl p-2 shadow-lg border border-white/20 backdrop-blur-sm">
						<div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-200 to-indigo-200 flex items-center justify-center">
							<img src={logo} alt="" className="rounded-lg w-6 h-6" />
						</div>
					</div>
				</div>
				<Col md={12}>
					<div className="h-screen overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-900">
						<div className="relative z-10 max-w-lg mx-auto p-6">
							<div className="mb-8 text-center">
								<div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 mb-4">
									<TrophyFilled className="text-yellow-400 text-sm" />
									<span className="text-white text-xs font-medium">
										Trusted by 1000+ businesses
									</span>
								</div>
								<h2 className="text-3xl font-bold text-white mb-3 leading-tight">
									<span className="bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
										Launch Your Own AI Receptionist Brand
									</span>{' '}
									with Frontdesk.
								</h2>

								<p className="text-white text-sm mb-3">
									Offering a full‑service White‑Label AI Receptionist Program — 24/7 virtual
									front‑desk powered by AI, branded as yours.
								</p>

								<div className="my-5 bg-white/20 backdrop-blur-sm rounded-lg text-white px-3 py-1">
									<h3 className="bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent text-2xl font-bold my-4">
										What You Get
									</h3>
									<p className="flex my-0 items-center mb-4 mt-5">
										<CheckCircleFilled size={20} className="me-3 text-yellow-500" /> Full
										white‑label branding — domain, on‑brand UX/UI, voices
									</p>
									<p className="flex my-0 items-center mb-4">
										<CheckCircleFilled size={20} className="me-3 text-yellow-500" /> Seamless
										integration with Stripe, CRMs, calendar systems
									</p>
									<p className="flex my-0 items-center mb-4">
										<CheckCircleFilled size={20} className="me-3 text-yellow-500" /> Unlimited
										workflows & API access
									</p>
									<p className="flex my-0 items-center mb-4">
										<CheckCircleFilled size={20} className="me-3 text-yellow-500" /> Dedicated
										success team, weekly check-ins, 24/7 chat support
									</p>
								</div>

								<div className="p-3 bg-white rounded-lg overflow-hidden hidden md:block">
									<iframe
										title="vimeo-player"
										src="https://player.vimeo.com/video/1019016722?h=c51588e3c5"
										width="100%"
										height="250"
										allowFullScreen
										className="rounded-xl"
									/>
								</div>
							</div>
						</div>
					</div>
				</Col>
				<Col md={12}>
					<div className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 h-auto md:h-screen">
						{renderStep()}
					</div>
				</Col>
			</Row>
		</>
	);
};

export default ResellerQualificationForm;
